import { VARIABLES } from 'wb-reddit/styles/variables';
import { startCase } from 'lodash';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import {
	Calendar, Pin, translate, UserIcon,
} from '@aleph/shared-components';
import { GlobeIcon, HashtagIcon, MusicNoteIcon } from '@heroicons/react/solid';
import {
	SpotifyMetric,
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_WITHOUT_DIMENSIONS,
	SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS,
	SPOTIFY_SUPERGRID_STATISTICS_DATE_DIMENSIONS,
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_FIELDS,
	SPOTIFY_SUPERGRID_STATISTICS_LIFETIME_METRICS,
} from 'wb-spotify/app/routes/superGrid/statistics/utils';

interface SpotifyMetricOption {
	value: SpotifyMetric;
	label: string;
	type: 'area' | 'bar' | 'line';
}

/**
 * These are the metrics which work with lifetime and date breakdowns.
 * See for more info:
 * https://alephrnd.atlassian.net/wiki/spaces/TECHD/pages/938344455/Statistics+Report+Types+Info?search_id=35a3a752-4235-4446-b511-ee903957ae04
 */
export const DASHBOARD_KPI_METRICS = [
	'clicks',
	'ctr',
	'impressions',
	'intentRate',
	'listeners',
	'newListeners',
	'newListenerConversionRate',
	'reach',
	'streams',
	'videoViews',
	'videoExpands',
	'videoExpandRate',
];

export const DASHBOARD_KPI_OPTIONS = DASHBOARD_KPI_METRICS.map((metric) => ({
	value: metric,
	label: translate(
		// TODO: use different translation for dashboard kpi metrics
		`sp: chart metrics ${metric}`,
		{
			defaultValue: startCase(metric),
		},
	),
}));

export const chartMetrics: SpotifyMetricOption[] = SPOTIFY_SUPERGRID_STATISTICS_METRICS_WITHOUT_DIMENSIONS.map(
	(metric) => ({
		value: metric,
		label: translate(
			`sp: chart metrics ${metric}`,
			{
				defaultValue: startCase(metric),
			},
		),
		type: 'area',
	}),
);

export function getChartMetrics(dimension: string) {
	let metrics = SPOTIFY_SUPERGRID_STATISTICS_METRICS_FIELDS;

	// @ts-ignore
	if (!SPOTIFY_SUPERGRID_STATISTICS_DATE_DIMENSIONS.includes(dimension) && dimension !== 'time') {
		// @ts-ignore
		metrics = SPOTIFY_SUPERGRID_STATISTICS_LIFETIME_METRICS;
	}

	return metrics.map((metric) => ({
		value: metric,
		label: translate(
			`sp: chart metrics ${metric}`,
			{
				defaultValue: startCase(metric),
			},
		),
		type: 'area',
	}));
}

const breakdownIcons = {
	age: <UserIcon width={16}
		height={16}
	/>,
	country: <GlobeIcon width={16}
		height={16}
	/>,
	platform: <Pin width={16}
		height={16}
	/>,
	gender: <HashtagIcon width={16}
		height={16}
	/>,
	genre: <MusicNoteIcon width={16}
		height={16}
	/>,
	date: <Calendar width={16}
		height={16}
	/>,
	dayOfMonth: <Calendar width={16}
		height={16}
	/>,
	weekdayNumber: <Calendar width={16}
		height={16}
	/>,
	weekdayName: <Calendar width={16}
		height={16}
	/>,
	weekNumber: <Calendar width={16}
		height={16}
	/>,
	monthNumber: <Calendar width={16}
		height={16}
	/>,
	monthName: <Calendar width={16}
		height={16}
	/>,
	quarter: <Calendar width={16}
		height={16}
	/>,
	year: <Calendar width={16}
		height={16}
	/>,
	yearMonth: <Calendar width={16}
		height={16}
	/>,
};

export const breakdownTypes = SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS.map((metric) => ({
	label: startCase(metric),
	value: metric,
	icon:  breakdownIcons[metric],
}));

export const kpiOptions = chartMetrics;

export const PIE_LEVEL_COLORS = {
	[SpotifyLevel.AD_ACCOUNT]: [
		VARIABLES.COLORS.BLUE[500],
		VARIABLES.COLORS.BLUE[600],
		VARIABLES.COLORS.BLUE[700],
		VARIABLES.COLORS.BLUE[800],
		VARIABLES.COLORS.BLUE[400],
		VARIABLES.COLORS.BLUE[300],
		VARIABLES.COLORS.BLUE[200],
	],
	[SpotifyLevel.CAMPAIGN]: [
		VARIABLES.COLORS.VIOLET[500],
		VARIABLES.COLORS.VIOLET[600],
		VARIABLES.COLORS.VIOLET[700],
		VARIABLES.COLORS.VIOLET[800],
		VARIABLES.COLORS.VIOLET[400],
		VARIABLES.COLORS.VIOLET[300],
		VARIABLES.COLORS.VIOLET[200],
	],
	[SpotifyLevel.GROUP]: [
		VARIABLES.COLORS.CYAN[500],
		VARIABLES.COLORS.CYAN[600],
		VARIABLES.COLORS.CYAN[700],
		VARIABLES.COLORS.CYAN[800],
		VARIABLES.COLORS.CYAN[400],
		VARIABLES.COLORS.CYAN[300],
		VARIABLES.COLORS.CYAN[200],
	],
	[SpotifyLevel.AD]: [
		VARIABLES.COLORS.APPLE_GREEN[500],
		VARIABLES.COLORS.APPLE_GREEN[600],
		VARIABLES.COLORS.APPLE_GREEN[700],
		VARIABLES.COLORS.APPLE_GREEN[800],
		VARIABLES.COLORS.APPLE_GREEN[400],
		VARIABLES.COLORS.APPLE_GREEN[300],
		VARIABLES.COLORS.APPLE_GREEN[200],
	],
};

export const LEVEL_COLORS = {
	[SpotifyLevel.AD_ACCOUNT]: [
		VARIABLES.COLORS.BLUE[600],
		VARIABLES.COLORS.GRAY[700],
		VARIABLES.COLORS.TURQUOISE[600],
		VARIABLES.COLORS.PURPLE[500],
		VARIABLES.COLORS.YELLOW[500],
		VARIABLES.COLORS.GRAY[900],
	],
	[SpotifyLevel.CAMPAIGN]: [
		VARIABLES.COLORS.VIOLET[500],
		VARIABLES.COLORS.GRAY[700],
		VARIABLES.COLORS.ROSE[500],
		VARIABLES.COLORS.TURQUOISE[600],
		VARIABLES.COLORS.YELLOW[500],
		VARIABLES.COLORS.GRAY[900],
	],
	[SpotifyLevel.GROUP]: [
		VARIABLES.COLORS.CYAN[600],
		VARIABLES.COLORS.GRAY[700],
		VARIABLES.COLORS.ROSE[500],
		VARIABLES.COLORS.YELLOW[500],
		VARIABLES.COLORS.TURQUOISE[700],
		VARIABLES.COLORS.GRAY[900],
	],
	[SpotifyLevel.AD]: [
		VARIABLES.COLORS.APPLE_GREEN[600],
		VARIABLES.COLORS.GRAY[700],
		VARIABLES.COLORS.ROSE[500],
		VARIABLES.COLORS.TURQUOISE[700],
		VARIABLES.COLORS.YELLOW[500],
		VARIABLES.COLORS.GRAY[900],
	],
};

export const getColorWithoutHSL = (color: string) => {
	const regex = /var\(\s*(--[\w-]+)\s*\)/;

	const match = color.match(regex);

	if (match && match[1]) return match[1];

	return color;
};

export const getCssVariable = (val: string): string => getComputedStyle(document.documentElement).getPropertyValue(val).trim();

// highchart can't access css variables directly, so we need to convert them to hsl format
export const getChartColor = (
	{
		idx = 0,
		level,
		count,
	}: {
		idx?: number | undefined;
		level?: SpotifyLevel;
		faded?: boolean;
		count?: number;
	},
	pieChart?: boolean,
) => {
	if (!level) {
		return `hsl(${getCssVariable(getColorWithoutHSL(VARIABLES.COLORS.GRAY[700]))})`;
	}

	return !pieChart
		? `hsl(${getCssVariable(getColorWithoutHSL(LEVEL_COLORS?.[level]?.[idx % 6]))})`
		: count && count === 1
			? `hsl(${getCssVariable(getColorWithoutHSL(PIE_LEVEL_COLORS?.[level]?.[3]))})`
			: `hsl(${getCssVariable(getColorWithoutHSL(PIE_LEVEL_COLORS?.[level]?.[idx % 7]))})`;
};
